// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

import xngGaGeneralEventEvent from "@guestsupply/App/Handlers/GoogleAnalytics/GeneralEvents/GeneralEvents";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { GOOGLE_EVENTS } from "@guestsupply/XDL/constants";
import { mergeWithCss } from "@guestsupply/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import { getIsPunchOutSession, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadCurrentBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadCurrentBillTo";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

interface linkGroups {
    title: string;
    links: (
        | {
              title: string;
              url: string;
              type?: string | undefined;
              id: string;
              parentId?: string | undefined;
              excludeFromNavigation?: boolean | undefined;
              children?: PageLinkModel[] | undefined;
              openInNewWindow?: boolean;
          }
        | undefined
    )[];
}

interface MiniSignInProps {
    linkGroups?: linkGroups[];
    onSignOutHandler: () => void;
    extendedStyles?: MiniMyAccountStyles;
}

const mapStateToProps = (state: CustomAppState) => ({
    // xng-change: add google event
    xGoogleAnalytics: state.xGoogleAnalytics,
});

type Props = MiniSignInProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface MiniMyAccountStyles {
    wrapper?: InjectableCss;
    mainTitle?: TypographyPresentationProps;
    titleClickable?: ClickablePresentationProps;
    linksSectionWrapper?: InjectableCss;
    linkItems?: {
        wrapper?: InjectableCss;
        linkWrapper?: InjectableCss;
        link?: LinkPresentationProps;
    };
    profileInfoWrapper?: InjectableCss;
    customerNameLabel?: TypographyPresentationProps;
    customerNameValue?: TypographyPresentationProps;
    accountNumberLabel?: TypographyPresentationProps;
    accountNumberValue?: TypographyPresentationProps;
    subTitle?: TypographyPresentationProps;
    actionsWrapper?: InjectableCss;
    changeCustomerButton?: ButtonPresentationProps;
    signOutButton?: ButtonPresentationProps;
}

export const miniMyAccountStyles: MiniMyAccountStyles = {};

const mapDispatchToProps = {
    loadCurrentBillTo,
    // xng-change: add google event
    xngGaGeneralEventEvent,
};

const MiniMyAccount: FC<Props> = ({
    linkGroups,
    onSignOutHandler,
    extendedStyles,
    loadCurrentBillTo,
    // xng-change: add google event
    xGoogleAnalytics,
    xngGaGeneralEventEvent,
}) => {
    const [styles] = React.useState(() => mergeWithCss(miniMyAccountStyles, extendedStyles));
    const history = useHistory();

    const { isPunchOutSession, displayChangeCustomerLink, changeCustomerUrl, currentBillTo } = useAppSelector(state => {
        return {
            currentBillTo: getCurrentBillToState(state),
            displayChangeCustomerLink: getSession(state).displayChangeCustomerLink,
            changeCustomerUrl: getPageLinkByPageType(state, "ChangeCustomerPage")?.url,
            isPunchOutSession: getIsPunchOutSession(state),
        };
    });

    useEffect(() => {
        if (!currentBillTo.value && !currentBillTo.isLoading) {
            loadCurrentBillTo();
        }
    }, []);

    const linksSection = linkGroups?.map((linkGroup, index) => {
        if (linkGroup.links.length > 0) {
            return (
                // eslint-disable-next-line react/no-array-index-key
                <StyledWrapper {...styles.linkItems?.wrapper} key={index}>
                    {linkGroup.title && <Typography {...styles.subTitle}>{linkGroup.title}</Typography>}
                    {linkGroup.links.map(
                        link =>
                            link?.url && (
                                <StyledWrapper key={`${link?.title}_${link?.url}`} {...styles.linkItems?.linkWrapper}>
                                    <Link
                                        onClick={() => {
                                            hideAll();
                                            // xng-change: add google event
                                            xngGaGeneralEventEvent({
                                                eventType: GOOGLE_EVENTS.MY_ACCOUNT_FLYOUT,
                                                includeEvent: xGoogleAnalytics.includeMyAccountFlyout,
                                                clickText: link?.title,
                                                destination: link?.url,
                                            });
                                        }}
                                        href={link?.url}
                                        {...styles.linkItems?.link}
                                        target={(link as any).openInNewWindow === true ? "_blank" : ""}
                                        aria-label={link?.title}
                                    >
                                        {link?.title}
                                    </Link>
                                </StyledWrapper>
                            ),
                    )}
                </StyledWrapper>
            );
        }
        return null;
    });

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.mainTitle}>{translate("Account Dashboard")}</Typography>
            <StyledWrapper {...styles.linksSectionWrapper}>{linksSection}</StyledWrapper>
            {currentBillTo.value && (
                <StyledWrapper {...styles.profileInfoWrapper}>
                    <Typography {...styles.subTitle}>{translate("Profile Information")}</Typography>
                    <StyledWrapper>
                        <Typography {...styles.customerNameLabel}>{`${translate("Customer Name")}: `}</Typography>
                        <Typography {...styles.customerNameValue}>{currentBillTo.value?.companyName}</Typography>
                    </StyledWrapper>

                    <StyledWrapper>
                        <Typography {...styles.accountNumberLabel}>{`${translate("Account Number")}: `}</Typography>
                        <Typography {...styles.accountNumberValue}>{currentBillTo.value?.customerNumber}</Typography>
                    </StyledWrapper>
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.actionsWrapper}>
                {displayChangeCustomerLink && (
                    <Button
                        {...styles.changeCustomerButton}
                        aria-label={translate("Change Account")}
                        onClick={() => {
                            changeCustomerUrl && history.push(changeCustomerUrl);
                            hideAll();
                        }}
                    >
                        {translate("Change Account")}
                    </Button>
                )}
                <Button
                    {...styles.signOutButton}
                    onClick={() => {
                        onSignOutHandler();
                        hideAll();
                    }}
                    aria-label={translate("Sign Out")}
                    data-test-selector="headerSignOutLink"
                >
                    {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(MiniMyAccount);
